import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, CheckCircle, Edit, FileText, Film, GripVertical, Image as ImageIcon, Loader2, Plus, RefreshCw, Save, Trash2, Upload, X } from 'lucide-react';
import { contentAPI, forumsAPI } from '../api/client';
import ConfirmModal from '../components/ConfirmModal';
import type { ForumListItem, Teaching, TeachingCreate, TeachingVisibility } from '../types';

type Mode = 'write' | 'upload';
type VideoRole = 'short_video' | 'full_video';

interface FormState {
  title: string;
  summary: string;
  body: string;
  author: string;
  categories: string;
  visibility: TeachingVisibility;
  scope_forum: number | null;
  show_in_library: boolean;
  content_date: string;
}

function isYouTubeUrl(url?: string): boolean {
  return Boolean(url && /youtu\.be|youtube\.com/i.test(url));
}

// Ingestion pipeline stages, mapped to a friendly label and rough progress so
// the status column reads as a moving progress bar instead of a bare spinner.
const IN_FLIGHT_STATUSES = ['pending', 'processing', 'extracting', 'chunking', 'embedding'];
const STAGE_PROGRESS: Record<string, { pct: number; label: string }> = {
  pending: { pct: 12, label: 'Queued' },
  processing: { pct: 45, label: 'Transcribing' },
  extracting: { pct: 45, label: 'Extracting' },
  chunking: { pct: 70, label: 'Preparing chunks' },
  embedding: { pct: 88, label: 'Indexing' },
};

function assetLabel(name?: string, url?: string): string {
  if (name) return name;
  if (!url) return '';
  const path = url.split('?')[0];
  return path.split('/').filter(Boolean).pop() || url;
}

const EMPTY: FormState = {
  title: '',
  summary: '',
  body: '',
  author: '',
  categories: '',
  visibility: 'org',
  scope_forum: null,
  show_in_library: true,
  content_date: '',
};

export default function TeachingsAdminPhase6() {
  const [teachings, setTeachings] = useState<Teaching[]>([]);
  const [forums, setForums] = useState<ForumListItem[]>([]);
  const [query, setQuery] = useState('');
  const [form, setForm] = useState<FormState>(EMPTY);
  const [mode, setMode] = useState<Mode>('write');
  const [editing, setEditing] = useState<Teaching | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Teaching | null>(null);
  const [contentSheet, setContentSheet] = useState<File | null>(null);
  const [growGuide, setGrowGuide] = useState<File | null>(null);
  const [growGuideUrl, setGrowGuideUrl] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [heroIndex, setHeroIndex] = useState(0);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [videoRole, setVideoRole] = useState<VideoRole>('short_video');
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderTab, setReorderTab] = useState<'general' | 'facilitator'>('general');
  const [generalOrder, setGeneralOrder] = useState<Teaching[]>([]);
  const [facilitatorOrder, setFacilitatorOrder] = useState<Teaching[]>([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const dragIndexRef = useRef<number | null>(null);

  const load = async () => {
    setTeachings(await contentAPI.getTeachings({ manage: true }));
  };

  useEffect(() => {
    load().catch(() => setMessage('Could not load teachings.'));
    forumsAPI.getAll().then(setForums).catch(() => setForums([]));
  }, []);

  // While anything is still ingesting, poll so the status column advances to
  // ready/failed on its own instead of requiring a manual page refresh.
  const hasInFlight = teachings.some((t) => IN_FLIGHT_STATUSES.includes(t.status));
  useEffect(() => {
    if (!hasInFlight) return;
    const id = setInterval(() => {
      load().catch(() => {});
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInFlight]);

  const visible = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return teachings;
    return teachings.filter((item) =>
      [item.title, item.summary, item.author, ...(item.categories || [])]
        .some((value) => value?.toLowerCase().includes(normalized)),
    );
  }, [query, teachings]);

  const resetAssets = () => {
    setContentSheet(null);
    setGrowGuide(null);
    setGrowGuideUrl('');
    setImages([]);
    setHeroIndex(0);
    setVideoFile(null);
    setYoutubeUrl('');
    setVideoRole('short_video');
  };

  const startCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setMode('write');
    resetAssets();
    setOpen(true);
  };

  const startEdit = (teaching: Teaching) => {
    resetAssets();
    setEditing(teaching);
    setForm({
      title: teaching.title,
      summary: teaching.summary,
      body: '',
      author: teaching.author,
      categories: teaching.categories.join(', '),
      visibility: teaching.visibility,
      scope_forum: teaching.scope_forum ?? null,
      show_in_library: teaching.show_in_library,
      content_date: teaching.content_date ?? '',
    });
    const youtube = [teaching.short_video_url, teaching.full_video_url, teaching.video_url]
      .find(isYouTubeUrl) || '';
    setYoutubeUrl(youtube);
    // An external Grow Guide link (as opposed to an uploaded file) has a URL
    // but no stored filename.
    if (teaching.grow_guide_url && !teaching.grow_guide_filename) {
      setGrowGuideUrl(teaching.grow_guide_url);
    }
    if (teaching.full_video_url && !teaching.short_video_url) {
      setVideoRole('full_video');
    }
    setMode('write');
    setOpen(true);
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (form.visibility === 'forum' && !form.scope_forum) {
      setMessage('Choose a Forum for forum-scoped content.');
      return;
    }
    if (videoFile && youtubeUrl.trim()) {
      setMessage('Choose either a direct video upload or a YouTube URL.');
      return;
    }

    setSaving(true);
    try {
      const payload: TeachingCreate = {
        title: form.title.trim(),
        summary: form.summary.trim(),
        author: form.author.trim(),
        categories: form.categories.split(',').map((item) => item.trim()).filter(Boolean),
        visibility: form.visibility,
        scope_forum: form.visibility === 'forum' ? form.scope_forum : null,
        show_in_library: form.show_in_library,
        content_date: form.content_date || null,
        grow_guide_url: growGuideUrl.trim(),
      };
      if (!editing || form.body.trim()) payload.content = form.body.trim();
      const teaching = editing
        ? await contentAPI.updateTeaching(editing.id, payload)
        : await contentAPI.createTeaching(payload);

      if (contentSheet) {
        await contentAPI.uploadAsset(teaching.id, 'content_sheet', { file: contentSheet });
      }
      if (growGuide) {
        await contentAPI.uploadAsset(teaching.id, 'grow_guide', { file: growGuide });
      }
      for (const [index, image] of images.entries()) {
        await contentAPI.uploadAsset(teaching.id, 'image', {
          file: image,
          is_hero: index === heroIndex,
          sort_order: index,
        });
      }
      if (videoFile) {
        setUploadPct(0);
        await contentAPI.uploadAsset(
          teaching.id,
          videoRole,
          { file: videoFile },
          (fraction) => setUploadPct(Math.round(fraction * 100)),
        );
        setUploadPct(null);
      } else if (youtubeUrl.trim()) {
        await contentAPI.uploadAsset(teaching.id, videoRole, {
          youtube_url: youtubeUrl.trim(),
        });
      }

      setMessage(editing ? 'Teaching updated.' : 'Teaching created.');
      setOpen(false);
      await load();
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { error?: string } } })
        ?.response?.data?.error;
      setMessage(detail || 'Could not save teaching.');
    } finally {
      setSaving(false);
      setUploadPct(null);
    }
  };

  const startReorder = () => {
    const library = teachings.filter((t) => t.show_in_library);
    setGeneralOrder(library.filter((t) => t.visibility !== 'admins_only'));
    setFacilitatorOrder(library.filter((t) => t.visibility === 'admins_only'));
    setReorderTab('general');
    setReorderMode(true);
  };

  const orderList = reorderTab === 'general' ? generalOrder : facilitatorOrder;
  const setOrderList = reorderTab === 'general' ? setGeneralOrder : setFacilitatorOrder;

  const moveItem = (from: number, to: number) => {
    if (to < 0 || to >= orderList.length) return;
    setOrderList((list) => {
      const next = [...list];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const saveOrder = async () => {
    const ids = [...generalOrder, ...facilitatorOrder].map((t) => t.id);
    if (ids.length === 0) return;
    setIsSavingOrder(true);
    try {
      await contentAPI.reorderTeachings(ids);
      setMessage('Library order saved.');
      setReorderMode(false);
      await load();
    } catch {
      setMessage('Could not save the new order. Please try again.');
    } finally {
      setIsSavingOrder(false);
    }
  };

  const retry = async (teaching: Teaching) => {
    await contentAPI.retryIngestion(teaching.id);
    setMessage('Ingestion retry queued.');
    await load();
  };

  const remove = async () => {
    if (!confirmDelete) return;
    await contentAPI.deleteTeaching(confirmDelete.id);
    setConfirmDelete(null);
    await load();
  };

  const openCurrentFile = async (url?: string, filename?: string) => {
    if (!url) return;
    try {
      await contentAPI.openAssetUrl(url, filename);
    } catch {
      setMessage('Could not open that file.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy">Knowledge Base</h1>
          <p className="text-gray-600">Manage Library media and private Ask IF knowledge.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!reorderMode && (
            <button
              onClick={startReorder}
              className="inline-flex items-center gap-2 rounded-lg border border-navy/30 px-4 py-2 text-navy hover:bg-navy/5"
            >
              <GripVertical size={18} /> Reorder Library
            </button>
          )}
          <button onClick={startCreate} className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-2 text-white">
            <Plus size={18} /> Add content
          </button>
        </div>
      </div>

      {message && (
        <div className="flex items-center gap-2 rounded-lg bg-navy/5 p-3 text-sm text-navy">
          <CheckCircle size={16} /> {message}
        </div>
      )}

      {reorderMode && (
        <div className="space-y-4 rounded-2xl bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="font-semibold text-navy">Arrange the Library</h2>
              <p className="text-sm text-gray-500">
                Drag items (or use the arrows) in each tab, then save. General content
                is the member Library; facilitator content is leadership-only.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setReorderMode(false)}
                disabled={isSavingOrder}
                className="rounded-lg px-4 py-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={saveOrder}
                disabled={isSavingOrder || (generalOrder.length + facilitatorOrder.length) === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-navy px-4 py-2 text-white disabled:opacity-50"
              >
                {isSavingOrder ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Save order
              </button>
            </div>
          </div>
          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => setReorderTab('general')}
              className={`flex-1 py-2 text-sm font-medium border-b-2 transition ${
                reorderTab === 'general'
                  ? 'border-navy text-navy'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              General content
              {generalOrder.length > 0 && (
                <span className="ml-1.5 text-gray-400">{generalOrder.length}</span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setReorderTab('facilitator')}
              className={`flex-1 py-2 text-sm font-medium border-b-2 transition ${
                reorderTab === 'facilitator'
                  ? 'border-navy text-navy'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Facilitator content
              {facilitatorOrder.length > 0 && (
                <span className="ml-1.5 text-gray-400">{facilitatorOrder.length}</span>
              )}
            </button>
          </div>
          {orderList.length === 0 ? (
            <p className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
              {reorderTab === 'general'
                ? 'No general Library items to arrange.'
                : 'No facilitator Library items to arrange.'}
            </p>
          ) : (
          <ul className="overflow-hidden rounded-xl border border-gray-100 divide-y divide-gray-100">
            {orderList.map((teaching, index) => (
              <li
                key={teaching.id}
                draggable
                onDragStart={() => { dragIndexRef.current = index; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  const from = dragIndexRef.current;
                  dragIndexRef.current = null;
                  if (from !== null && from !== index) moveItem(from, index);
                }}
                className="flex cursor-grab items-center gap-3 bg-white px-4 py-3 hover:bg-gray-50 active:cursor-grabbing"
              >
                <GripVertical size={16} className="shrink-0 text-gray-300" />
                <span className="w-7 shrink-0 text-sm text-gray-400">{index + 1}.</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-gray-900">{teaching.title}</p>
                  <p className="text-xs text-gray-500">{teaching.content_date || 'No content date'}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => moveItem(index, index - 1)}
                    disabled={index === 0}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-navy disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <ArrowUp size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveItem(index, index + 1)}
                    disabled={index === orderList.length - 1}
                    className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-navy disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <ArrowDown size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
          )}
        </div>
      )}

      {!reorderMode && (
      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search teachings…"
        className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-navy"
      />
      )}

      {!reorderMode && (
      <div className="overflow-x-auto rounded-2xl bg-white shadow-sm">
        <table className="w-full">
          <thead className="bg-off-white text-left text-sm text-navy">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Visibility</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map((teaching) => (
              <tr key={teaching.id}>
                <td className="px-4 py-3">
                  <p className="font-medium">{teaching.title}</p>
                  <p className="line-clamp-1 text-sm text-gray-500">{teaching.summary}</p>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {teaching.show_in_library ? 'Library' : 'Ask IF only'} · {teaching.visibility}
                </td>
                <td className="px-4 py-3 text-sm">
                  {IN_FLIGHT_STATUSES.includes(teaching.status) ? (
                    <IngestionProgress status={teaching.status} />
                  ) : (
                    <>
                      {teaching.status === 'ready' && (
                        <CheckCircle size={14} className="mr-1 inline text-green-600" />
                      )}
                      {teaching.status}
                      {teaching.status === 'failed' && teaching.error_message && (
                        <p className="max-w-xs text-xs text-red-600">{teaching.error_message}</p>
                      )}
                    </>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    {teaching.status === 'failed' && (
                      <button onClick={() => retry(teaching)} className="p-2" aria-label="Retry ingestion">
                        <RefreshCw size={17} />
                      </button>
                    )}
                    <button onClick={() => startEdit(teaching)} className="p-2" aria-label="Edit">
                      <Edit size={17} />
                    </button>
                    <button onClick={() => setConfirmDelete(teaching)} className="p-2 text-red-600" aria-label="Delete">
                      <Trash2 size={17} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <form onSubmit={save} className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h2 className="text-xl font-bold text-navy">{editing ? 'Edit content' : 'Add content'}</h2>
                <p className="mt-0.5 text-sm text-gray-500">
                  {editing ? 'Current files stay until you replace them.' : 'Title is required. Everything else is optional.'}
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100" aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5">
              <section className="space-y-4">
                <Field label="Title" required>
                  <input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} className={fieldClass} />
                </Field>
                <Field label="Summary" hint="Shown in the Library reader. Transcripts stay private.">
                  <textarea value={form.summary} onChange={(event) => setForm({ ...form, summary: event.target.value })} rows={3} className={fieldClass} />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Author">
                    <input value={form.author} onChange={(event) => setForm({ ...form, author: event.target.value })} className={fieldClass} />
                  </Field>
                  <Field label="Categories" hint="Comma-separated, e.g. Leadership, Prayer">
                    <input value={form.categories} onChange={(event) => setForm({ ...form, categories: event.target.value })} placeholder="Leadership, Prayer" className={fieldClass} />
                  </Field>
                  <Field label="Content date" hint="Used to order the Library when no manual order is set.">
                    <input type="date" value={form.content_date} onChange={(event) => setForm({ ...form, content_date: event.target.value })} className={fieldClass} />
                  </Field>
                </div>
              </section>

              {!editing && (
                <section>
                  <p className="mb-2 text-sm font-medium text-navy">How are you adding this?</p>
                  <div className="grid grid-cols-2 gap-2 rounded-xl bg-off-white p-1">
                    {([
                      { id: 'write' as Mode, label: 'Write text', hint: 'Paste a teaching' },
                      { id: 'upload' as Mode, label: 'Attach files', hint: 'PDFs, images, video' },
                    ]).map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setMode(item.id)}
                        className={`rounded-lg px-3 py-2.5 text-left ${mode === item.id ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`}
                      >
                        <span className="block text-sm font-semibold">{item.label}</span>
                        <span className="block text-xs font-normal">{item.hint}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {(mode === 'write' || editing) && (
                <Field label="Teaching body" hint="Private knowledge for Ask IF and search. Hidden from Library readers.">
                  <textarea value={form.body} onChange={(event) => setForm({ ...form, body: event.target.value })} rows={7} className={fieldClass} />
                </Field>
              )}

              {(mode === 'upload' || editing) && (
                <section className="space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-navy">Files</h3>
                    <p className="text-xs text-gray-500">
                      {editing ? 'Choose a replacement only if you want to change a file.' : 'Add any combination of PDFs, images, or a video.'}
                    </p>
                  </div>
                  <AssetField
                    icon={FileText}
                    label="Content sheet"
                    accept=".pdf,application/pdf"
                    file={contentSheet}
                    onFile={setContentSheet}
                    currentName={editing?.content_sheet_filename}
                    currentUrl={editing?.content_sheet_url}
                    onOpenCurrent={() => openCurrentFile(editing?.content_sheet_url, editing?.content_sheet_filename)}
                  />
                  <div className="rounded-xl border border-gray-200 p-3 space-y-3">
                    <AssetField
                      icon={FileText}
                      label="Grow guide"
                      accept=".pdf,application/pdf"
                      file={growGuide}
                      onFile={(file) => {
                        setGrowGuide(file);
                        if (file) setGrowGuideUrl('');
                      }}
                      currentName={editing?.grow_guide_filename || (editing?.has_grow_guide ? assetLabel('', editing.grow_guide_url) : undefined)}
                      currentUrl={editing?.grow_guide_url}
                      onOpenCurrent={() => openCurrentFile(
                        editing?.grow_guide_url,
                        editing?.grow_guide_filename || assetLabel('', editing?.grow_guide_url),
                      )}
                      embedded
                    />
                    <Field label="Or paste a Grow Guide link" hint="Members see a “Do the Grow Guide” button that opens this link.">
                      <input
                        type="url"
                        value={growGuideUrl}
                        onChange={(event) => {
                          setGrowGuideUrl(event.target.value);
                          if (event.target.value) setGrowGuide(null);
                        }}
                        placeholder="https://…"
                        className={fieldClass}
                      />
                    </Field>
                  </div>

                  <div className="rounded-xl border border-gray-200 p-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-off-white text-navy">
                        <ImageIcon size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-navy">Images</p>
                        <p className="text-xs text-gray-500">
                          The Hero image is the Library thumbnail. Uploaded videos without one show a frame from the video.
                        </p>
                        {Boolean(editing?.images?.length) && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {editing?.images?.map((image) => (
                              <span key={image.id} className="relative">
                                <AuthThumb src={image.url} />
                                {image.is_hero && (
                                  <span className="absolute inset-x-0 bottom-0 rounded-b-lg bg-navy/80 text-center text-[10px] text-white">
                                    Hero
                                  </span>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                        {images.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {images.map((image, index) => (
                              <li key={`${image.name}-${index}`}>
                                <label className="flex items-center gap-2 text-sm text-gray-700">
                                  <input type="radio" name="hero-image" checked={heroIndex === index} onChange={() => setHeroIndex(index)} />
                                  <span className="truncate">{image.name}</span>
                                  {heroIndex === index && <span className="text-xs text-gold">Hero</span>}
                                </label>
                              </li>
                            ))}
                          </ul>
                        )}
                        {!editing?.images?.length && images.length === 0 && (
                          <p className="text-sm text-gray-400">None yet</p>
                        )}
                      </div>
                      <ImagePickerButton
                        label={editing?.images?.length || images.length ? 'Add' : 'Choose'}
                        onFiles={(files) => {
                          setImages(files);
                          setHeroIndex(0);
                        }}
                      />
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 p-3 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-off-white text-navy">
                        <Film size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-navy">Video</p>
                        {editing && (editing.short_video_url || editing.full_video_url || editing.video_url) ? (
                          <div className="mt-1 space-y-0.5 text-sm text-gray-600">
                            {editing.short_video_url && (
                              <p>
                                Short:{' '}
                                <AssetLink url={editing.short_video_url} onOpen={() => openCurrentFile(editing.short_video_url)} />
                              </p>
                            )}
                            {editing.full_video_url && (
                              <p>
                                Full:{' '}
                                <AssetLink url={editing.full_video_url} onOpen={() => openCurrentFile(editing.full_video_url)} />
                              </p>
                            )}
                            {editing.video_url && !editing.short_video_url && !editing.full_video_url && (
                              <p>
                                <AssetLink url={editing.video_url} onOpen={() => openCurrentFile(editing.video_url)} />
                              </p>
                            )}
                          </div>
                        ) : (
                          !videoFile && !youtubeUrl && <p className="text-sm text-gray-400">None yet</p>
                        )}
                      </div>
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {([
                        { id: 'short_video' as VideoRole, label: 'Short video' },
                        { id: 'full_video' as VideoRole, label: 'Full video' },
                      ]).map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setVideoRole(item.id)}
                          className={`rounded-lg border px-3 py-2 text-sm ${videoRole === item.id ? 'border-navy bg-navy/5 font-medium text-navy' : 'border-gray-200 text-gray-600'}`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                    <AssetField
                      icon={Film}
                      label="Upload a file"
                      accept="video/mp4,video/webm,video/quicktime,.m4v"
                      file={videoFile}
                      onFile={(file) => {
                        setVideoFile(file);
                        if (file) setYoutubeUrl('');
                      }}
                      embedded
                    />
                    <Field label="Or paste a YouTube URL">
                      <input
                        type="url"
                        value={youtubeUrl}
                        onChange={(event) => {
                          setYoutubeUrl(event.target.value);
                          if (event.target.value) setVideoFile(null);
                        }}
                        placeholder="https://youtube.com/watch?v=…"
                        className={fieldClass}
                      />
                    </Field>
                  </div>
                </section>
              )}

              <section className="rounded-xl border border-gray-200 p-4">
                <h3 className="mb-3 text-sm font-semibold text-navy">Who can see this</h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Library">
                    <select
                      value={form.show_in_library ? 'library' : 'jarvis'}
                      onChange={(event) => setForm({ ...form, show_in_library: event.target.value === 'library' })}
                      className={fieldClass}
                    >
                      <option value="library">Show in Library</option>
                      <option value="jarvis">Ask IF knowledge only</option>
                    </select>
                  </Field>
                  <Field label="Audience">
                    <select
                      value={form.visibility}
                      onChange={(event) => setForm({ ...form, visibility: event.target.value as TeachingVisibility })}
                      className={fieldClass}
                    >
                      <option value="org">Organization-wide</option>
                      <option value="forum">Forum-scoped</option>
                      <option value="admins_only">Admins/Facilitators only</option>
                    </select>
                  </Field>
                </div>
                {form.visibility === 'forum' && (
                  <div className="mt-4">
                    <Field label="Forum">
                      <select
                        value={form.scope_forum ?? ''}
                        onChange={(event) => setForm({ ...form, scope_forum: Number(event.target.value) || null })}
                        className={fieldClass}
                      >
                        <option value="">Choose a Forum…</option>
                        {forums.map((forum) => <option key={forum.id} value={forum.id}>{forum.name}</option>)}
                      </select>
                    </Field>
                  </div>
                )}
              </section>
            </div>

            <div className="border-t border-gray-200 bg-white px-6 py-4">
              {uploadPct !== null && (
                <div className="mb-3">
                  <div className="flex items-center justify-between text-xs font-medium text-gray-600">
                    <span>Uploading video…</span>
                    <span>{uploadPct}%</span>
                  </div>
                  <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-navy transition-all duration-300"
                      style={{ width: `${uploadPct}%` }}
                    />
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-gray-600 hover:bg-gray-50">
                  Cancel
                </button>
                <button disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-navy px-5 py-2 text-white disabled:opacity-50">
                  {saving ? <Loader2 size={17} className="animate-spin" /> : mode === 'upload' && !editing ? <Upload size={17} /> : <Save size={17} />}
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <ConfirmModal
        isOpen={Boolean(confirmDelete)}
        title="Delete Teaching"
        message={`Delete “${confirmDelete?.title || ''}”?`}
        onConfirm={remove}
        onCancel={() => setConfirmDelete(null)}
        confirmLabel="Delete"
      />
    </div>
  );
}

function IngestionProgress({ status }: { status: string }) {
  const stage = STAGE_PROGRESS[status] ?? { pct: 15, label: status };
  return (
    <div className="w-40">
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
        <Loader2 size={12} className="shrink-0 animate-spin" />
        {stage.label}…
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full animate-pulse rounded-full bg-navy transition-all duration-700"
          style={{ width: `${stage.pct}%` }}
        />
      </div>
    </div>
  );
}

const fieldClass =
  'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-navy';

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-navy">
        {label}
        {required ? <span className="text-gold"> *</span> : null}
      </span>
      {children}
      {hint ? <span className="mt-1 block text-xs font-normal text-gray-500">{hint}</span> : null}
    </label>
  );
}

function ChooseButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 rounded-lg border border-navy/20 px-3 py-1.5 text-sm font-medium text-navy hover:bg-navy/5"
    >
      {label}
    </button>
  );
}

function AssetField({
  icon: Icon,
  label,
  accept,
  file,
  onFile,
  currentName,
  currentUrl,
  onOpenCurrent,
  embedded,
}: {
  icon: typeof FileText;
  label: string;
  accept: string;
  file: File | null;
  onFile: (file: File | null) => void;
  currentName?: string;
  currentUrl?: string;
  onOpenCurrent?: () => void;
  embedded?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const displayName = file?.name || currentName;

  return (
    <div className={embedded ? '' : 'rounded-xl border border-gray-200 p-3'}>
      <div className="flex items-start gap-3">
        {!embedded && (
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-off-white text-navy">
            <Icon size={18} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-navy">{label}</p>
          {displayName ? (
            <p className="truncate text-sm text-gray-600">
              {currentUrl && !file && onOpenCurrent ? (
                <button type="button" onClick={onOpenCurrent} className="text-navy underline">
                  {displayName}
                </button>
              ) : (
                displayName
              )}
              {file && currentName ? <span className="text-gold"> · replacing</span> : null}
            </p>
          ) : (
            <p className="text-sm text-gray-400">None yet</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ChooseButton label={displayName ? 'Replace' : 'Choose'} onClick={() => inputRef.current?.click()} />
          {file ? (
            <button
              type="button"
              onClick={() => {
                onFile(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Clear file"
            >
              <X size={16} />
            </button>
          ) : null}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(event) => onFile(event.target.files?.[0] || null)}
      />
    </div>
  );
}

function AssetLink({ url, onOpen }: { url: string; onOpen: () => void }) {
  if (isYouTubeUrl(url)) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="text-navy underline">
        {assetLabel(undefined, url)}
      </a>
    );
  }
  return (
    <button type="button" onClick={onOpen} className="text-navy underline">
      {assetLabel(undefined, url)}
    </button>
  );
}

function AuthThumb({ src }: { src: string }) {
  const [objectUrl, setObjectUrl] = useState('');

  useEffect(() => {
    let revoked = '';
    let cancelled = false;
    contentAPI.fetchAssetObjectUrl(src).then((url) => {
      if (cancelled) {
        if (url.startsWith('blob:')) window.URL.revokeObjectURL(url);
        return;
      }
      if (url.startsWith('blob:')) revoked = url;
      setObjectUrl(url);
    }).catch(() => {
      if (!cancelled) setObjectUrl('');
    });
    return () => {
      cancelled = true;
      if (revoked) window.URL.revokeObjectURL(revoked);
    };
  }, [src]);

  if (!objectUrl) return <span className="block h-14 w-14 rounded-lg border bg-off-white" />;
  return <img src={objectUrl} alt="" className="h-14 w-14 rounded-lg border object-cover" />;
}

function ImagePickerButton({
  label,
  onFiles,
}: {
  label: string;
  onFiles: (files: File[]) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <ChooseButton label={label} onClick={() => inputRef.current?.click()} />
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        onChange={(event) => onFiles(Array.from(event.target.files || []))}
      />
    </>
  );
}
